import FullCalendar from '@fullcalendar/react';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import rrulePlugin from '@fullcalendar/rrule';
import PropTypes from 'prop-types';
import { useState, useRef } from 'react';
import { DialogTitle, Dialog, Box, Alert } from '@mui/material';
import { useDispatch, useSelector } from '../../../../redux/store';
import { selectEvent, selectRange, onCloseModal } from '../../../../redux/slices/vendors';
import { CalendarForm, StyledCalendar, CalendarToolbar } from '../../../../reusable-components/calendar';
import ListViewGrid from './ListViewGrid';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import LoadingAnimation from '../../../../reusable-components/loader/LoadingAnimation';

CalendarPage.propTypes = {
  events: PropTypes.array,
  handleEditAddDate: PropTypes.func,
  handleDelete: PropTypes.func,
  view: PropTypes.string,
  loading: PropTypes.bool,
  setView: PropTypes.func,
  facility: PropTypes.object,
  vendor: PropTypes.object,
  setVendor: PropTypes.func,
  setFacility: PropTypes.func,
};

export default function CalendarPage({ events, loading, handleEditAddDate, handleDelete, setView, view, facility, vendor, setVendor, setFacility }) {
  const dispatch = useDispatch();
  const calendarRef = useRef(null);
  const { openModal, selectedRange, selectedEventId } = useSelector((state) => state.vendors);

  const [date, setDate] = useState(new Date());

  const selectedEvent = selectedEventId ? events.find((event) => event.id === parseInt(selectedEventId, 10)) : null;

  const handleClickToday = () => {
    const calendarEl = calendarRef.current;
    if (!calendarEl) return;
    const calendarApi = calendarEl.getApi();
    calendarApi.today();
    setDate(calendarApi.getDate());
  };

  const handleChangeView = (newView) => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      if (newView === 'dayGridMonth') calendarApi.changeView(newView);
      else calendarApi.destroy();
    }
    setView(newView);
  };

  const handleClickDatePrev = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.prev();
      setDate(calendarApi.getDate());
    }
  };

  const handleClickDateNext = () => {
    const calendarEl = calendarRef.current;
    if (!calendarEl) return; 
    const calendarApi = calendarEl.getApi();
    calendarApi.next();
    setDate(calendarApi.getDate());
  };

  const handleSelectRange = (arg) => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.unselect();
    }
    dispatch(selectRange({ start: arg.start, end: arg.end }));
  };

  const handleSelectEvent = (arg) => dispatch(selectEvent(arg.event.id));
  const handleCloseModal = () => dispatch(onCloseModal());

  const toManyEvents = events.filter(x => x.weekly || x.biweekly).length > 100;
  const eventsToDisplay = toManyEvents ? [...events.filter(x => !(x.weekly || x.biweekly)), ...events.filter(x => x.weekly || x.biweekly).slice(0, 100)] : events;

  return <>
    <StyledCalendar>
      <CalendarToolbar
        date={date}
        view={view}
        onNextDate={handleClickDateNext}
        onPrevDate={handleClickDatePrev}
        onToday={handleClickToday}
        onChangeView={handleChangeView}
        vendor={vendor}
        facility={facility}
        setVendor={setVendor}
        setFacility={setFacility}
      />
      {view === 'dayGridMonth' ? loading ? <LoadingAnimation /> : <Box sx={{ pb: 3 }} >
        {toManyEvents && <Alert sx={{ width: '100%', height: '35px', fontSize: 12, py: 0, px: 1, m: 0, my: 1 }} severity={"error"}>Too many events to display properly. Use list view to see all events.</Alert>}
        <FullCalendar
          weekends
          selectable          
          selectAllow={(selectInfo) => (selectInfo.end - selectInfo.start) / (24 * 60 * 60 * 1000) <= 1}
          allDayMaintainDuration
          eventResizableFromStart
          events={eventsToDisplay}
          ref={calendarRef}
          initialDate={date}
          initialView={view}
          dayMaxEventRows={3}
          eventDisplay="block"
          headerToolbar={false}
          select={handleSelectRange}
          eventClick={handleSelectEvent}
          height={'auto'}
          plugins={[ dayGridPlugin, interactionPlugin, rrulePlugin]}
        />
      </Box> : <ListViewGrid />}
    </StyledCalendar>

    <Dialog
      fullWidth
      maxWidth="xs"
      open={openModal}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleCloseModal();
      }}
    >
      <DialogTitle>
        {selectedEvent ? 'Edit Event' : 'Add Event'}
        <CloseIconButton onClick={handleCloseModal} />
      </DialogTitle>

      <CalendarForm
        event={selectedEvent}
        range={selectedRange}
        onClose={handleCloseModal}
        handleEditAddDate={handleEditAddDate}
        onDelete={handleDelete}
        view={view}
        facility={facility}
        vendor={vendor}
      />
    </Dialog>
  </>;
}
